import type { StatusTone } from '@vnetwork/ui/data';
import type { Contact, OrgAdmin } from '@/api/organization';

export type MemberOrgRole = 'Owner' | 'Admin' | 'Member';

/** Match an org-admin row to a contact by login id, falling back to contact id. */
export function adminForContact(contact: Contact, admins: OrgAdmin[]): OrgAdmin | undefined {
  return admins.find(
    (a) => (contact.user_uuid && a.user_uuid === contact.user_uuid) || a.contact_id === contact.contact_id,
  );
}

/** Derive a member's org-wide role from the primary flag + the org-admins join. */
export function memberOrgRole(contact: Contact, admins: OrgAdmin[]): MemberOrgRole {
  if (contact.is_primary) return 'Owner';
  const a = adminForContact(contact, admins);
  if (a?.role === 'owner') return 'Owner';
  if (a?.role === 'admin') return 'Admin';
  return 'Member';
}

export function memberRoleTone(role: MemberOrgRole): 'primary' | 'accent' | 'neutral' {
  if (role === 'Owner') return 'primary';
  if (role === 'Admin') return 'accent';
  return 'neutral';
}

export interface AccountState {
  label: string;
  tone: StatusTone;
}

/**
 * Account sign-in state shown as a StatusPill. Today only `has_login` is
 * available, so members resolve to Active / No login. Backend lifecycle flags
 * (enabled, pending invite, require_2fa) will extend this to Invited / Disabled
 * / 2FA required — the pill vocabulary is designed for that (spec §2.2).
 */
export function accountState(contact: Contact): AccountState {
  if (!contact.has_login) return { label: 'No login', tone: 'muted' };
  return { label: 'Active', tone: 'success' };
}
